import type { LineupChannel, LineupPack } from "./channel-lineup.types";
import type { LineupSearchSource } from "./lineup-search.shared";

/**
 * The visitor's locals and in-market RSNs, already shaped as organism channels. They load in the
 * browser after first paint, so the server render never waits on the visitor's market.
 */
export interface LineupMarketRows {
  localChannels: readonly LineupChannel[];
  /** Only RSNs with a ✓ in a signature column — v2 drops the rest. */
  rsns: readonly LineupChannel[];
  /** Every market channel a Genre pack may list: the locals and all in-market RSNs. */
  packChannels: readonly LineupChannel[];
}

export const EMPTY_MARKET_ROWS: LineupMarketRows = {
  localChannels: [],
  rsns: [],
  packChannels: []
};

/** GP6's channel order. Hoisted: a collator built per comparison is the measured cost. */
const PACK_NAME_COLLATOR = new Intl.Collator("en", { sensitivity: "base" });

export function byPackChannelName(a: LineupChannel, b: LineupChannel): number {
  return PACK_NAME_COLLATOR.compare(a.name, b.name);
}

function hasRows(rows: LineupMarketRows): boolean {
  return rows.localChannels.length > 0 || rows.rsns.length > 0 || rows.packChannels.length > 0;
}

/** The grid's channels plus this market's rows, locals before RSNs. */
export function withMarketChannels(
  channels: LineupChannel[],
  rows: LineupMarketRows
): LineupChannel[] {
  if (!hasRows(rows)) {
    return channels;
  }
  const merged = [...channels];
  const seen = new Set(merged.map(channel => channel.id));
  for (const channel of [...rows.localChannels, ...rows.rsns]) {
    if (!seen.has(channel.id)) {
      seen.add(channel.id);
      merged.push(channel);
    }
  }
  return merged;
}

/**
 * Each pack plus the market channels filed under its code. A pack with nothing to add is returned
 * as-is, so memoised children keep their identity.
 */
export function withMarketPackChannels(
  packs: LineupPack[],
  channels: readonly LineupChannel[]
): LineupPack[] {
  if (channels.length === 0) {
    return packs;
  }
  return packs.map(pack => {
    const present = new Set(pack.channels.map(channel => channel.id));
    const added = channels.filter(channel => (
      !present.has(channel.id) &&
      !!channel.packages &&
      Object.prototype.hasOwnProperty.call(channel.packages, pack.billingProductCode)
    ));
    return added.length === 0 ? pack : {
      ...pack,
      channels: [...pack.channels, ...added].sort(byPackChannelName)
    };
  });
}

/** The search source with this market's rows folded in. */
export function withMarketSource(
  source: LineupSearchSource,
  rows: LineupMarketRows
): LineupSearchSource {
  if (!hasRows(rows)) {
    return source;
  }
  return {
    ...source,
    localChannels: rows.localChannels,
    rsns: rows.rsns,
    packs: source.packs ? withMarketPackChannels([...source.packs], rows.packChannels) : source.packs
  };
}
